import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  isNetworkError,
  NetworkError,
  getNetworkErrorMessage,
  fetchWithTimeout,
  TimeoutError,
  getTimeoutErrorMessage
} from '../utils/networkError';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Storage helper functions for token management
const getStoredToken = () => {
  // Check localStorage first (persistent), then sessionStorage (session-only)
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

const getStoredRememberMe = () => {
  return localStorage.getItem('rememberMe') === 'true';
};

const saveToken = (token, rememberMe = false) => {
  // Clear both storages first to avoid conflicts
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');

  if (rememberMe) {
    localStorage.setItem('token', token);
    localStorage.setItem('rememberMe', 'true');
  } else {
    sessionStorage.setItem('token', token);
    localStorage.removeItem('rememberMe');
  }
};

const clearToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('rememberMe');
  sessionStorage.removeItem('token');
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);

  // Check auth status on mount (with timeout handling)
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetchWithTimeout(
            `${API_URL}/auth/me`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            },
            10000 // 10 second timeout for auth check
          );

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Token invalid, clear it
            clearToken();
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check error:', error);
          // Don't clear token on timeout - allow retry on next navigation
          if (!(error instanceof TimeoutError)) {
            clearToken();
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const register = async (formData) => {
    let response;
    try {
      response = await fetchWithTimeout(
        `${API_URL}/auth/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        },
        20000 // 20 second timeout for registration
      );
    } catch (error) {
      // Handle timeout and network errors with friendly messages
      if (error instanceof TimeoutError) {
        throw new TimeoutError(getTimeoutErrorMessage());
      }
      if (isNetworkError(error)) {
        throw new NetworkError(getNetworkErrorMessage());
      }
      throw error;
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.errors?.[0]?.msg || 'Registration failed');
    }

    // Store token and user (registration defaults to persistent session)
    saveToken(data.token, true);
    setToken(data.token);
    setUser(data.user);

    return data;
  };

  const login = async (email, password, rememberMe = false) => {
    let response;
    try {
      response = await fetchWithTimeout(
        `${API_URL}/auth/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, password })
        },
        15000 // 15 second timeout for login
      );
    } catch (error) {
      // Handle timeout and network errors with friendly messages
      if (error instanceof TimeoutError) {
        throw new TimeoutError(getTimeoutErrorMessage());
      }
      if (isNetworkError(error)) {
        throw new NetworkError(getNetworkErrorMessage());
      }
      throw error;
    }

    const data = await response.json();

    if (!response.ok) {
      // For 401 errors (invalid credentials), show the specific error
      // For other errors, show a generic credentials message
      const errorMessage = response.status === 401
        ? (data.error || 'Invalid credentials')
        : 'Invalid email or password. Please try again.';
      throw new Error(errorMessage);
    }

    // Store token based on rememberMe preference
    // If rememberMe is true, use localStorage (persists across browser restart)
    // If rememberMe is false, use sessionStorage (clears when browser closes)
    saveToken(data.token, rememberMe);
    setToken(data.token);
    setUser(data.user);

    return data;
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      setToken(null);
      setUser(null);
    }
  };

  // Google OAuth login - called after successful Google OAuth
  const googleLogin = async (googleData) => {
    // googleData should contain { token, user } from the Google OAuth flow
    if (googleData && googleData.token && googleData.user) {
      saveToken(googleData.token, true); // Always remember Google logins
      setToken(googleData.token);
      setUser(googleData.user);
      return googleData;
    }
    throw new Error('Invalid Google authentication data');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    googleLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
